// src/components/PrivateRoute.jsx
import React, { useEffect, useMemo } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import api from '../api/axios';

const JUST_LOGGED_OUT_FLAG = 'auth:justLoggedOut';

const PrivateRoute = ({ children }) => {
  const location = useLocation();

  const justLoggedOut = useMemo(() => {
    try { return sessionStorage.getItem(JUST_LOGGED_OUT_FLAG) === '1'; } catch { return false; }
  }, []);

  const query = useQuery({
    queryKey: ['auth', 'profile'],
    queryFn: async () => {
      const res = await api.get('/auth/profile');
      return res.data;
    },
    enabled: !justLoggedOut,          // still call the hook; just skip the fetch
    retry: false,
    // 🔒 Auth must be fresh; disable cache + always refetch on mount/focus
    staleTime: 0,
    cacheTime: 0,                      // v4 (tanstack) — for v5 use gcTime: 0
    refetchOnMount: 'always',
    refetchOnWindowFocus: 'always',
    keepPreviousData: false,
  });

  // treat any initial fetch in-flight as loading (prevents cached-flash)
  const initialLoading = query.isLoading || (query.isFetching && !query.isFetched);

  useEffect(() => {
    if (justLoggedOut) {
      try { sessionStorage.removeItem(JUST_LOGGED_OUT_FLAG); } catch {}
    }
  }, [justLoggedOut]);

  if (justLoggedOut) {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }

  if (initialLoading) {
    return <div className="p-6 text-gray-600">Loading…</div>;
  }

  if (query.isError || !query.data) {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }

  return children;
};

export default PrivateRoute;
